import { useCallback, useState, useEffect, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cleave from "cleave.js/react";
import { CreditCardType } from "cleave.js/options/creditCard";
import { ReactComponent as Amex } from "@fortawesome/fontawesome-free/svgs/brands/cc-amex.svg";
import { ReactComponent as Visa } from "@fortawesome/fontawesome-free/svgs/brands/cc-visa.svg";
import { ReactComponent as DinersClub } from "@fortawesome/fontawesome-free/svgs/brands/cc-diners-club.svg";
import { ReactComponent as Discover } from "@fortawesome/fontawesome-free/svgs/brands/cc-discover.svg";
import { ReactComponent as JCB } from "@fortawesome/fontawesome-free/svgs/brands/cc-jcb.svg";
import { ReactComponent as Mastercard } from "@fortawesome/fontawesome-free/svgs/brands/cc-mastercard.svg";

import { ValidatedFormFieldConfig } from "@evergiving/common/dist/es2015/form";

import { RootState } from "@/lib/redux/types";
import { setFormValue, resetFormErrors } from "@/lib/redux/actions";
import { LabelledField } from "@/lib/components/LabelledField";
import { LabelText } from "@/lib/components/LabelText";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { InputFieldConfig } from "@/lib/misc/InputFieldConfig";
import { FieldError } from "@/lib/components/FieldError";
import { useAutofill } from "@/lib/hooks/useAutofill";

const creditCardIcons: Partial<
  {
    [key in CreditCardType]: React.ReactElement;
  }
> = {
  amex: <Amex className="creditCardIcon" />,
  diners: <DinersClub className="creditCardIcon" />,
  discover: <Discover className="creditCardIcon" />,
  jcb: <JCB className="creditCardIcon" />,
  mastercard: <Mastercard className="creditCardIcon" />,
  visa: <Visa className="creditCardIcon" />,
};

export function CreditCardNumberField({
  config,
  autoFocus,
}: {
  config: ValidatedFormFieldConfig;
} & InputFieldConfig): ReactElement {
  const name = "cardNumber";
  const required = !!config.requiredFieldErrorText;
  const valueFromStore = useSelector((state: RootState) => state[name].value);
  const [currentValue, setCurrentValue] = useState<string>(
    valueFromStore || ""
  );
  const anyErrors = useSelector((state: RootState) => state.errors[name]);
  const dispatch = useDispatch();
  const [isDirty, setIsDirty] = useState(false);

  const fieldValidationErrors = useFieldValidationErrors(config);

  const saveData = useCallback(
    (toSave: string) => {
      function normalizeFieldValue(toNormalize: string): string | undefined {
        const trimmed = toNormalize.trim();
        if (trimmed.length > 0) {
          return trimmed.replace(/\D/g, "");
        }
        return undefined;
      }

      const newValue = normalizeFieldValue(toSave);

      dispatch(
        setFormValue(
          name,
          newValue,
          fieldValidationErrors.forValue(newValue).length > 0
        )
      );

      setIsDirty(true);
    },
    [dispatch, fieldValidationErrors, name]
  );

  const {
    detectAutofillByAnimation,
    handleBlurWithAutofill,
    handleFocusWithAutofill,
    handleInputWithAutofillSafariHack,
    saveDataWithAutofill,
  } = useAutofill({ saveData });

  // Cleave does something to the original onChange that disrupts the autofill detection hack
  const handleChangeCapture = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      saveDataWithAutofill(value);
    },
    [saveDataWithAutofill]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentValue(event.target.value);
      dispatch(resetFormErrors(name));
    },
    [dispatch, name]
  );

  useEffect(() => {
    setCurrentValue(valueFromStore || "");
  }, [valueFromStore]);

  const [
    currentIconClass,
    setCurrentIconClass,
  ] = useState<React.ReactElement>();

  const handleCreditCardTypeChanged = useCallback((type: CreditCardType) => {
    setCurrentIconClass(creditCardIcons[type]);
  }, []);

  const errorId = `${name}-errors`;

  const inputField = (
    <>
      {/* eslint-disable jsx-a11y/no-autofocus */}
      {/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^ Discussion of this override follows:

       We're only passing through the attribute here.  See DonationDetailsPage for
       a discussion why we would ever want to set this attribute.

       */}
      <div className="creditCardNumberContainer">
        <Cleave
          options={{
            creditCard: true,
            onCreditCardTypeChanged: handleCreditCardTypeChanged,
          }}
          className="textField"
          type="text"
          name="cardnumber"
          placeholder={config.placeholderText}
          value={currentValue}
          onAnimationStart={detectAutofillByAnimation}
          onBlur={handleBlurWithAutofill}
          onChange={handleChange}
          onChangeCapture={handleChangeCapture}
          onFocus={handleFocusWithAutofill}
          onInput={handleInputWithAutofillSafariHack}
          autoComplete="cc-number"
          autoFocus={autoFocus}
          inputMode="numeric"
          spellCheck="false"
          onInit={(owner) => {
            // https://github.com/nosir/cleave.js/issues/601#issuecomment-902747682
            // eslint-disable-next-line no-param-reassign
            (owner as any).lastInputValue = "";
          }}
          aria-required={required}
          aria-invalid={(anyErrors && isDirty) || undefined}
          aria-describedby={anyErrors ? errorId : undefined}
        />
        <div className="creditCardIconOverlay">{currentIconClass}</div>
      </div>
      {/* eslint-enable jsx-a11y/no-autofocus */}
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(currentValue)}
          />
        ) : null}
      </div>
    </>
  );

  return (
    <LabelledField id={name}>
      <LabelText requiredIndicator={required}>{config.labelText}</LabelText>
      {inputField}
    </LabelledField>
  );
}
